import React from "react";
import { css } from "@emotion/react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { GOOGLE_API_KEY } from "main/javascripts/constants/Constants";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { CruiseRouteListMarker } from "./CruiseRouteListMarker";
import { CruiseRouteListMapPolyline } from "./CruiseRouteListMapPolyline";
import { Cruise } from "main/javascripts/types/cruise";

export interface IProps {
  cruiseRoutes: Cruise.CruiseRoute[];
  heightStyle?: string;
}

export const CruiseRouteListDynamicMap: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cruiseRoutes, heightStyle } = props;

  const getMiddlePosition = (prev, next) => {
    if (prev && next) {
      const lat1 = Number(prev.lat);
      const lon1_original = Number(prev.lng);
      const lat2 = Number(next.lat);
      const lon2_original = Number(next.lng);

      // 経度を比較し、必要なら一方を±360度シフト
      let lon1 = lon1_original;
      let lon2 = lon2_original;
      if (Math.abs(lon1 - lon2) > 180) {
        if (lon1 > lon2) {
          lon2 += 360;
        } else {
          lon1 += 360;
        }
      }

      // 緯度は通常通り平均
      const latitude = (lat1 + lat2) / 2;

      // 経度は補正後の値で平均し、-180～180度範囲内に正規化
      let midpointLongitude = (lon1 + lon2) / 2;
      if (midpointLongitude > 180) {
        midpointLongitude -= 360;
      } else if (midpointLongitude <= -180) {
        midpointLongitude += 360;
      }

      const longitude = midpointLongitude;
      return {
        lat: latitude,
        lng: longitude,
      };
    }
    return null;
  };

  const findNearestPosition = (routes, currentIndex) => {
    const prev = routes
      .slice(0, currentIndex)
      .reverse()
      .find((route) => route?.port?.latitude && route?.port?.longitude);

    const next = routes
      .slice(currentIndex + 1)
      .find((route) => route?.port?.latitude && route?.port?.longitude);

    return getMiddlePosition(
      prev
        ? { lat: Number(prev.port.latitude), lng: Number(prev.port.longitude) }
        : null,
      next
        ? { lat: Number(next.port.latitude), lng: Number(next.port.longitude) }
        : null
    );
  };

  const markerData = (() => {
    const positionCountMap = {};
    return cruiseRoutes.map((cruiseRoute, index) => {
      let position = null;

      const isInvalid =
        cruiseRoute.routeType === "sailing" ||
        !cruiseRoute.port ||
        !cruiseRoute.port.latitude ||
        !cruiseRoute.port.longitude;

      if (!isInvalid) {
        position = {
          lat: Number(cruiseRoute.port.latitude),
          lng: Number(cruiseRoute.port.longitude),
        };
      } else {
        position = findNearestPosition(cruiseRoutes, index);
      }

      const positionKey = position ? `${position.lat},${position.lng}` : null;
      const duplicateCount = positionKey
        ? positionCountMap[positionKey] || 0
        : 0;
      if (positionKey) positionCountMap[positionKey] = duplicateCount + 1;

      return {
        day: cruiseRoute.day,
        title: cruiseRoute.title,
        position: position,
        duplicateCount: duplicateCount,
      };
    });
  })();

  const coordinates = markerData.map((data) => data.position);

  // 平均経度を0度にシフトする関数
  const shiftLongitude = (lon, center) => {
    let shifted = lon - center;
    // [-180, 180]に収まるよう正規化
    shifted = ((shifted + 180) % 360) - 180;
    return shifted;
  };

  const calculateZoom = (locations) => {
    const latitudes = locations.map((loc) => loc.lat);
    const longitudes = locations.map((loc) => loc.lng);

    // 経度の平均値を求める
    const avgLon = longitudes.reduce((a, b) => a + b, 0) / longitudes.length;

    const shiftedLongitudes = longitudes.map((lon) =>
      shiftLongitude(lon, avgLon)
    );

    // シフト後の経度の最小・最大を取得
    const maxLon = Math.max(...shiftedLongitudes);
    const minLon = Math.min(...shiftedLongitudes);

    // 緯度はそのまま最小・最大を取得
    const maxLat = Math.max(...latitudes);
    const minLat = Math.min(...latitudes);

    const latDiff = maxLat - minLat;
    const lonDiff = maxLon - minLon; // ここでは既に短い弧で計算される

    // 縦方向が小さいため、latitude差は2倍して判定
    const maxDiff = Math.max(latDiff * 2, lonDiff);
    if (maxDiff > 200) return 1; // 世界一周クルーズ（超広域）
    if (maxDiff > 100) return 2; // 世界一周クルーズ（超広域）
    if (maxDiff > 40) return 3; // 地中海クルーズ（広域）
    if (maxDiff > 15) return 4; // 地中海クルーズ（広域）
    if (maxDiff > 7) return 5; // 地域周遊（中広域）
    if (maxDiff > 4) return 6; // 地域周遊（中広域）
    if (maxDiff > 2) return 7; // 短距離クルーズ（狭域）
    return 8; // 短距離クルーズ（狭域）
  };

  function calculateCenter(coordinates) {
    // 度からラジアンへの変換関数
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;

    let x = 0;
    let y = 0;
    let z = 0;

    // 緯度経度を3Dベクトルに変換して加算
    coordinates.forEach((coord) => {
      const lat = toRad(coord.lat);
      const lon = toRad(coord.lng);
      x += Math.cos(lat) * Math.cos(lon);
      y += Math.cos(lat) * Math.sin(lon);
      z += Math.sin(lat);
    });

    const total = coordinates.length;
    x /= total;
    y /= total;
    z /= total;

    // 平均ベクトルから「中心」の緯度経度に戻す
    const hyp = Math.sqrt(x * x + y * y);
    const centerLat = Math.atan2(z, hyp);
    const centerLon = Math.atan2(y, x);

    return {
      lat: toDeg(centerLat),
      lng: toDeg(centerLon),
    };
  }

  const zoom = calculateZoom(coordinates);
  const center = calculateCenter(coordinates);

  const filteredMarkerData = markerData.filter(
    (data) => data.position !== null
  );

  let blockStyles = blockStyle;
  if (heightStyle) {
    blockStyles = css`
      ${blockStyles};
      height: ${heightStyle};
    `;
  }

  return (
    <APIProvider apiKey={GOOGLE_API_KEY}>
      <div css={blockStyles}>
        <Map
          mapTypeControl={false}
          streetViewControl={false}
          clickableIcons={false}
          defaultCenter={center}
          defaultZoom={zoom}
          mapId="ca303613a46cfe5a"
          gestureHandling="greedy" // ヒントの表示無効化
        >
          {filteredMarkerData.map((data, index) => {
            return (
              <CruiseRouteListMarker
                key={index}
                index={data.day}
                position={data.position}
                duplicateCount={data.duplicateCount}
              />
            );
          })}
          <CruiseRouteListMapPolyline coordinates={coordinates} />
        </Map>
      </div>
    </APIProvider>
  );
};

const blockStyle = css`
  width: auto;
  height: 18rem;
  border-radius: ${borderRadius.normal};
  overflow: hidden;
  position: relative;
  outline: none;
`;
